import { throttle } from 'lodash';
import { BaseElements, BaseObject, BaseService, BaseInjector } from '../../js/base';
import { querySelectorAll, querySelector } from '../utils';
import ParallexElement from './ParallaxElement';
import { EmptyNodeList } from '../utils/index';

class Texts extends BaseObject { }

class ClassList extends BaseObject { }

class Selectors extends BaseObject {
    parallax = '.parallax';
    parallaxTop = '.parallax--top';
    parallaxNonTop = '.parallax:not(.parallax--top)';
    noImage = '.parallax__element:not(.parallax--no-image)'
}

class Elements extends BaseElements<Selectors> {
    parallaxElementsTop: NodeListOf<HTMLElement> = new EmptyNodeList();
    parallaxElementsMiddle: NodeListOf<HTMLElement> = new EmptyNodeList();

    constructor(selectors: Selectors) {
        super(selectors, true);
    }

}

interface Parallex extends BaseService<Selectors, Elements, ClassList, Texts> { }

@BaseInjector(Selectors, Elements, ClassList, Texts)
class Parallex {
    sentinelCount: number;
    ioSupport: boolean;
    parallaxElements: ParallexElement[];

    get isEnabled(): boolean {
        const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
        return !(mediaQuery && mediaQuery.matches)
    }

    get thresholdArraySteps() {
        return 300
    }

    get thresholdArray() {
        const steps = this.thresholdArraySteps;
        return [...Array(steps + 1)].map((_, index) => index / steps || 0);
    }

    get observerConfig() {
        return {
            threshold: this.thresholdArray // Number of steps
        }
    }

    initialize(): void {
        if (!this.isEnabled) {
            return;
        }
        this.setElements();
        this.addEventListeners();
    }
    setElements() {
        this.parallaxElements = this.base.convertToArray(querySelectorAll(this.selectors.parallax)).map(this.initialzeParrallexElement.bind(this));
        this.elements.parallaxElementsTop = querySelectorAll(this.selectors.parallaxTop);
        this.elements.parallaxElementsMiddle = querySelectorAll(this.selectors.parallaxNonTop);
        this.sentinelCount = 0;
        this.ioSupport = 'IntersectionObserver' in window
            && 'IntersectionObserverEntry' in window
            && 'intersectionRatio' in window.IntersectionObserverEntry.prototype;
    }


    initialzeParrallexElement(element: HTMLElement): ParallexElement {
        return new ParallexElement(element);
    }

    addEventListeners(): void {
        if (!this.ioSupport) return;

        this.parallaxElements.forEach((parallax) => {
            this.initializeIO(parallax);
        });

        window.addEventListener('resize', throttle(() => {
            this.elementHeightUpdate();
        }, 32), { passive: true });
    }

    elementHeightUpdate() {
        this.parallaxElements.forEach((parallax) => {
            parallax.updateHeight();
            parallax.configureSentinel();
        });
    }


    initializeIO(parallax: ParallexElement) {
        parallax.target = querySelector(this.selectors.noImage);
        parallax.updateRect();
        this.sentinelCount = parallax.createSentinel(this.sentinelCount);
        const observerConfig = this.observerConfig;
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                this.ioHandler(parallax, entry);
            });
        }, observerConfig);
        observer.observe(parallax.sentinel);
    }



    ioHandler(parallax: ParallexElement, entry: IntersectionObserverEntry) {
        const isAbove = entry.boundingClientRect.y < entry.rootBounds.y;
        const useful = ((entry.boundingClientRect.height - parallax.height) / (2 * entry.boundingClientRect.height)); //eslint-disable-line
        let yPosition = ((1 - (1 - entry.intersectionRatio) / (1 - useful)) / 0.02);
        const yPositionPercent = isAbove ? 100 - yPosition : yPosition;
        yPosition = Number((yPositionPercent / 3).toFixed(4)) * parallax.reduceScrollingEffectBy;
        let top = ((entry.boundingClientRect.height + parallax.height) / 2) / entry.boundingClientRect.height; //eslint-disable-line
        if (top > 1) {
            top = 1;
        }
        if ((entry.intersectionRatio > useful && parallax.target)
            && (yPosition !== parallax.yPosition)) {
            if (parallax.isTop) {
                const topPosition = (1 - (entry.intersectionRatio - useful) / (top - useful)) * 33 * parallax.reduceScrollingEffectBy;
                parallax.target.style.transform = `translate3d(0, ${topPosition}% ,0)`;
            } else {
                parallax.target.style.transform = `translate3d(0, ${yPosition}% ,0)`;
            }
        }
        parallax.yPosition = yPosition;
    }



}

export default Parallex;

export {
    Elements as ParallexElements,
    Selectors as ParallexSelectors,
    ClassList as ParallexClassList,
    Texts as ParallexTexts
}